// Event-kind metadata. Single source of truth for the timeline glyphs and
// filter chips on the Past Recordings View.
//
// Validated against the backend: real AVA event kinds are `motion`, `ding`,
// `on_demand`. Person detection is a CV enrichment on a `motion` event
// (`person_detected: true`) — we surface it as a fifth visual kind on the
// timeline ("Person") even though the wire kind stays `motion`. "package"
// is NOT an AVA category in this service (LLD 13 §6).
//
// Glyphs distinguish each kind by SHAPE *and* color (a11y; LLD 13 §14).

export type EventKindId = "person" | "motion" | "ding" | "on_demand";

export interface KindMeta {
  /** Filter-chip label. */
  label: string;
  /** CSS class on the glyph element (matches `.gl-*` rules in recordings.css). */
  cls: string;
}

export const KINDS: Record<EventKindId, KindMeta> = {
  // Synthetic kind: motion + person_detected=true.
  person: { label: "Person", cls: "gl-person" },
  motion: { label: "Motion", cls: "gl-motion" },
  ding: { label: "Doorbell", cls: "gl-ding" },
  on_demand: { label: "On-demand", cls: "gl-ondemand" },
};

/** Iteration order for the filter-chip row. */
export const KIND_ORDER: readonly EventKindId[] = [
  "person",
  "motion",
  "ding",
  "on_demand",
];

/**
 * Map a backend event onto the visual kind it belongs to.
 *
 * The backend reports a single `kind` field (`motion`/`ding`/`on_demand`)
 * plus a `person_detected` boolean. The timeline lifts person-detected
 * motion to a separate visual kind so operators can filter on it directly.
 */
export function visualKindFor(
  ev: { kind?: string | null; person_detected?: boolean } | null | undefined,
): EventKindId | null {
  if (!ev) return null;
  const kind = (ev.kind ?? "").trim();
  if (ev.person_detected) return "person";
  if (kind === "motion" || kind.startsWith("motion.")) return "motion";
  if (kind === "ding") return "ding";
  if (kind === "on_demand") return "on_demand";
  return null;
}
